import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService, DPPData } from '../services/api';
import SnapshotDPPViewer from './SnapshotDPPViewer';
import { isPublishedSnapshot, type PublishedSnapshot } from '../types/snapshot';
import { detectViewerLocale, viewerT, type ViewerStringKey } from '../i18n/viewerI18n';
import '../styles/index.css';

type ViewerSectionKey = 'general' | 'technical' | 'sustainability' | 'compliance' | 'materials';

type SectionField = {
  key: string;
  label: string;
  value: string;
  sortOrder: number;
};

type TemplateAttribute = NonNullable<NonNullable<DPPData['template_categories']>[number]['attributes']>[number];

const RESERVED_METADATA_KEYS = new Set([
  'template',
  'template_category',
  'template_attribute_values_by_key',
  'template_attribute_values_by_id',
  'variant_defining_attribute_keys',
  'completeness',
]);

const DOCUMENT_LIKE_PATTERN = /certificate|certification|manual|datasheet|document|file|report|spec[_ -]?sheet|safety[_ -]?sheet/i;

const DEFAULT_BRAND_COLOR = '#10b981';

const normaliseHexColor = (value?: string | null, fallback = DEFAULT_BRAND_COLOR) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(trimmed) ? trimmed : fallback;
};

const hexToRgba = (value: string, alpha: number) => {
  const normalized = normaliseHexColor(value).slice(1);
  const fullHex = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;
  const red = parseInt(fullHex.slice(0, 2), 16);
  const green = parseInt(fullHex.slice(2, 4), 16);
  const blue = parseInt(fullHex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const humanizeKey = (value: string) =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isUuidLike = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const hasExplicitChannelRequirements = (requirements?: Partial<Record<'website' | 'datasheet' | 'dpp', boolean>> | null) =>
  !!requirements && Object.keys(requirements).length > 0;

const isAttributeVisibleInDpp = (attribute: TemplateAttribute) => {
  if (attribute.show_in_detail === false) return false;
  if (!hasExplicitChannelRequirements(attribute.channel_requirements)) return true;
  return Boolean(attribute.channel_requirements?.dpp);
};

const isEmptyValue = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0 || value.every((item) => isEmptyValue(item));
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0;
  return false;
};

const formatObjectValue = (value: Record<string, unknown>): string => {
  const entries = Object.entries(value).filter(([, entryValue]) => !isEmptyValue(entryValue));
  if (entries.length === 0) return '—';

  const orderedDimensionKeys = ['length', 'width', 'height', 'depth'];
  const hasDimensions = orderedDimensionKeys.some((key) => value[key] !== undefined);
  if (hasDimensions) {
    const dimensionValues = orderedDimensionKeys
      .map((key) => value[key])
      .filter((entry) => !isEmptyValue(entry))
      .map((entry) => String(entry));
    const unit = typeof value.unit === 'string' ? ` ${value.unit}` : '';
    if (dimensionValues.length > 0) {
      return `${dimensionValues.join(' × ')}${unit}`;
    }
  }

  return entries
    .slice(0, 6)
    .map(([entryKey, entryValue]) => `${humanizeKey(entryKey)}: ${formatValue(entryValue)}`)
    .join(' • ');
};

const formatValue = (value: unknown): string => {
  if (value == null) return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '—';
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    const formatted = value
      .map((item) => formatValue(item))
      .filter((item) => item !== '—');
    return formatted.length > 0 ? formatted.join(', ') : '—';
  }
  if (typeof value === 'object') {
    return formatObjectValue(value as Record<string, unknown>);
  }
  return String(value);
};

const inferSection = (...parts: Array<string | undefined | null>): ViewerSectionKey => {
  const text = parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/material|composition|substance|ingredient|packaging|alloy|fabric|plastic|metal|wood|glass/.test(text)) {
    return 'materials';
  }
  if (/sustain|environment|carbon|water|recycl|eco|emission|footprint|waste|renewable|biodegrad|end.?of.?life|circular/.test(text)) {
    return 'sustainability';
  }
  if (/compliance|certificate|certification|conform|regulation|standard|ce\b|rohs|reach|weee|declaration|safety|legal/.test(text)) {
    return 'compliance';
  }
  if (/technical|spec|dimension|size|weight|power|voltage|current|performance|mechanical|thermal|operating|connectivity|processor|screen|resolution|memory|storage|battery|capacity|warranty|software|application|display/.test(text)) {
    return 'technical';
  }
  return 'general';
};

const buildFieldBuckets = (dpp: DPPData) => {
  const buckets: Record<ViewerSectionKey, SectionField[]> = {
    general: [],
    technical: [],
    sustainability: [],
    compliance: [],
    materials: [],
  };
  const seen = new Set<string>();

  const pushField = (
    section: ViewerSectionKey,
    key: string,
    label: string,
    rawValue: unknown,
    sortOrder = 999,
  ) => {
    if (isEmptyValue(rawValue)) return;

    const formatted = formatValue(rawValue);
    if (formatted === '—') return;

    const dedupeKey = `${section}:${key}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);

    buckets[section].push({
      key,
      label,
      value: formatted,
      sortOrder,
    });
  };

  const product = dpp.product;
  const metadata = product?.metadata || {};
  const templateValues = metadata.template_attribute_values_by_key || {};
  const templateAttributesByKey = new Map<string, TemplateAttribute>();

  pushField('technical', 'weight', 'Weight', product?.weight, 5);
  pushField('technical', 'dimensions', 'Dimensions', product?.dimensions, 6);
  pushField('general', 'price', 'Price', product?.price, 20);

  const templateCategories = (dpp.template_categories || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  for (const category of templateCategories) {
    const attributes = (category.attributes || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    for (const attribute of attributes) {
      templateAttributesByKey.set(attribute.attribute_key, attribute);
      if (!isAttributeVisibleInDpp(attribute)) continue;

      const key = attribute.attribute_key;
      const rawValue = templateValues[key] ?? metadata[key];
      if (isEmptyValue(rawValue)) continue;

      if (
        typeof rawValue === 'string' &&
        isUuidLike(rawValue) &&
        DOCUMENT_LIKE_PATTERN.test(`${key} ${attribute.attribute_name} ${attribute.display_group || ''} ${category.category_name}`)
      ) {
        continue;
      }

      const section = inferSection(
        attribute.display_group,
        category.category_name,
        category.category_key,
        attribute.attribute_name,
        key,
      );

      pushField(section, key, attribute.attribute_name || humanizeKey(key), rawValue, attribute.sort_order || 999);
    }
  }

  for (const [key, rawValue] of Object.entries(metadata)) {
    if (RESERVED_METADATA_KEYS.has(key)) continue;
    const templateAttribute = templateAttributesByKey.get(key);
    if (templateAttribute && !isAttributeVisibleInDpp(templateAttribute)) continue;
    if (templateAttribute) continue;
    if (Object.prototype.hasOwnProperty.call(templateValues, key)) continue;
    if (typeof rawValue === 'string' && isUuidLike(rawValue) && DOCUMENT_LIKE_PATTERN.test(key)) continue;

    const section = inferSection(key);
    pushField(section, key, humanizeKey(key), rawValue, 999);
  }

  pushField('sustainability', 'carbon_footprint_kg_co2', 'Carbon Footprint', dpp.environmental?.carbon_footprint_kg_co2 != null ? `${dpp.environmental.carbon_footprint_kg_co2} kg CO2e` : null, 10);
  pushField('sustainability', 'water_usage_liters', 'Water Usage', dpp.environmental?.water_usage_liters != null ? `${dpp.environmental.water_usage_liters} L` : null, 20);
  pushField('sustainability', 'energy_consumption_kwh', 'Energy Consumption', dpp.environmental?.energy_consumption_kwh != null ? `${dpp.environmental.energy_consumption_kwh} kWh` : null, 30);
  pushField('sustainability', 'recyclability_percentage', 'Recyclability', dpp.environmental?.recyclability_percentage != null ? `${dpp.environmental.recyclability_percentage}%` : null, 40);
  pushField('sustainability', 'recycling_instructions', 'End-of-Life Instructions', dpp.endOfLife?.recycling_instructions, 50);
  pushField('sustainability', 'disposal_method', 'Disposal Method', dpp.endOfLife?.disposal_method, 60);
  pushField('sustainability', 'repair_instructions', 'Repair Instructions', dpp.endOfLife?.repair_instructions, 70);
  pushField('sustainability', 'reuse_instructions', 'Reuse Instructions', dpp.endOfLife?.reuse_instructions, 80);
  pushField('sustainability', 'spare_parts_availability', 'Spare Parts Available', dpp.endOfLife?.spare_parts_availability, 90);

  pushField('compliance', 'compliance_status', 'Compliance Status', dpp.dpp?.status ? humanizeKey(dpp.dpp.status) : null, 10);

  (Object.keys(buckets) as ViewerSectionKey[]).forEach((section) => {
    buckets[section].sort((left, right) => left.sortOrder - right.sortOrder || left.label.localeCompare(right.label));
  });

  return buckets;
};

const renderFieldGrid = (fields: SectionField[]) => (
  <div className="grid grid-2" role="list">
    {fields.map((field) => (
      <div className="grid-item" key={field.key} role="listitem">
        <div className="grid-label" id={`label-${field.key}`}>{field.label}</div>
        <div
          className="grid-value"
          style={{ fontSize: '16px', fontWeight: 500 }}
          aria-labelledby={`label-${field.key}`}
        >
          {field.value}
        </div>
      </div>
    ))}
  </div>
);

const DPPViewer: React.FC = () => {
  const { uniqueId } = useParams<{ uniqueId: string }>();
  const [dpp, setDpp] = useState<DPPData | null>(null);
  const [snapshot, setSnapshot] = useState<PublishedSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locale, setLocale] = useState<string>(() => detectViewerLocale());

  const t = (key: ViewerStringKey) => viewerT(key, locale);

  const sectionLabel = (key: ViewerSectionKey) => {
    const map: Record<ViewerSectionKey, ViewerStringKey> = {
      general: 'productDetails',
      technical: 'technicalSpecifications',
      sustainability: 'sustainabilityInformation',
      compliance: 'complianceCertifications',
      materials: 'materialComposition',
    };
    return t(map[key]);
  };

  useEffect(() => {
    const loadDPP = async () => {
      if (!uniqueId) return;

      try {
        setLoading(true);
        setError(null);
        setSnapshot(null);
        setDpp(null);
        const response = await apiService.publicDPP.getByUniqueId(uniqueId, locale);
        if (response.data.success && response.data.data) {
          if (isPublishedSnapshot(response.data.data)) {
            setSnapshot(response.data.data);
          } else {
            setDpp(response.data.data as DPPData);
          }
        } else {
          setError(t('notFound'));
        }
      } catch (err: any) {
        console.error('Error loading DPP:', err);
        setError(err.response?.data?.message || t('failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadDPP();
  }, [uniqueId, locale]);

  const metadata = dpp?.product?.metadata || {};
  const summary = dpp?.product?.description || dpp?.product?.short_description || 'No description provided.';
  const documents = dpp?.documents || [];
  const documentsByType = dpp?.documents_by_type || {};
  const dynamicFields = useMemo(() => (dpp ? buildFieldBuckets(dpp) : null), [dpp]);
  const headerManufacturer = metadata?.manufacturer || metadata?.brand;
  const branding = dpp?.branding;
  const brandColor = normaliseHexColor(branding?.primary_color);
  const brandName = branding?.organisation_name || headerManufacturer || 'Organisation';
  const brandLogo = branding?.logo_url || null;

  if (loading) {
    return (
      <div className="spinner" role="status" aria-live="polite" aria-label={t('loading')}>
        <div className="spinner-inner" />
      </div>
    );
  }

  if (snapshot) {
    return (
      <SnapshotDPPViewer
        snapshot={snapshot}
        activeLocale={locale}
        onLocaleChange={setLocale}
      />
    );
  }

  if (error || !dpp || !dynamicFields) {
    return (
      <div className="container">
        <div className="error" role="alert">
          <h2>{t('notFound')}</h2>
          <p>{error || t('notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <a href="#dpp-main" className="skip-link">{t('skipToContent')}</a>
      <div className="container" style={{ paddingTop: '24px', paddingBottom: '24px' }}>
        <main id="dpp-main" role="main" aria-labelledby="dpp-legacy-title">
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {brandLogo ? (
                <img src={brandLogo} alt={`${brandName} ${t('organisationLogo')}`} style={{ maxHeight: '44px', maxWidth: '140px', objectFit: 'contain' }} />
              ) : (
                <div style={{ fontSize: '18px', fontWeight: 700, color: brandColor }}>{brandName}</div>
              )}
            </div>
            <div
              style={{
                backgroundColor: hexToRgba(brandColor, 0.1),
                color: brandColor,
                borderRadius: '999px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              {t('publicDpp')}
            </div>
          </div>
          <h1 id="dpp-legacy-title" className="card-title">{dpp.product?.name || t('digitalProductPassport')}</h1>
          <p className="card-subtitle">
            {headerManufacturer ? `by ${headerManufacturer}` : dpp.dpp.unique_identifier}
          </p>
          <div className="mt-1">
            <span
              className={`badge ${dpp.dpp.status === 'published' ? 'badge-success' : 'badge-default'}`}
              style={dpp.dpp.status === 'published'
                ? { backgroundColor: hexToRgba(brandColor, 0.12), color: brandColor }
                : undefined}
            >
              {dpp.dpp.status}
            </span>
          </div>
        </div>

        <div className="grid grid-2">
          <div className="grid-item">
            <div className="grid-label">{t('sku')}</div>
            <div className="grid-value">{dpp.product?.sku || '—'}</div>
          </div>
          <div className="grid-item">
            <div className="grid-label">{t('category')}</div>
            <div className="grid-value">{dpp.product?.category_name || '—'}</div>
          </div>
          <div className="grid-item">
            <div className="grid-label">{t('uniqueId')}</div>
            <div className="grid-value" style={{ fontSize: '14px', wordBreak: 'break-word' }}>{dpp.dpp.unique_identifier}</div>
          </div>
          <div className="grid-item">
            <div className="grid-label">{t('published')}</div>
            <div className="grid-value">{dpp.dpp.published_at ? new Date(dpp.dpp.published_at).toLocaleDateString() : '—'}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="section-title">{t('productDescription')}</h2>
        <p>{summary}</p>
      </div>

      {dynamicFields.general.length > 0 && (
        <div className="card">
          <h2 className="section-title">{sectionLabel('general')}</h2>
          {renderFieldGrid(dynamicFields.general)}
        </div>
      )}

      {(dpp.materials?.length || dynamicFields.materials.length > 0) ? (
        <div className="card">
          <h2 className="section-title">{sectionLabel('materials')}</h2>
          {dpp.materials && dpp.materials.length > 0 && (
            <div className="table-container" style={{ marginBottom: dynamicFields.materials.length > 0 ? '16px' : 0 }}>
              <table className="table">
                <caption className="visually-hidden">{sectionLabel('materials')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('material')}</th>
                    <th scope="col">{t('type')}</th>
                    <th scope="col">{t('percentage')}</th>
                    <th scope="col">{t('weight')}</th>
                    <th scope="col">{t('origin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dpp.materials.map((material) => (
                    <tr key={material.id}>
                      <td>{material.material_name}</td>
                      <td>{material.material_type}</td>
                      <td>{material.percentage ? `${material.percentage}%` : '—'}</td>
                      <td>{material.weight_grams ? `${material.weight_grams} g` : '—'}</td>
                      <td>{material.origin_country || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {dynamicFields.materials.length > 0 && renderFieldGrid(dynamicFields.materials)}
        </div>
      ) : null}

      {dynamicFields.sustainability.length > 0 && (
        <div className="card">
          <h2 className="section-title">{sectionLabel('sustainability')}</h2>
          {renderFieldGrid(dynamicFields.sustainability)}
        </div>
      )}

      {(dpp.certificates?.length || dynamicFields.compliance.length > 0) ? (
        <div className="card">
          <h2 className="section-title">{sectionLabel('compliance')}</h2>
          {dpp.certificates && dpp.certificates.length > 0 && (
            <div className="table-container" style={{ marginBottom: dynamicFields.compliance.length > 0 ? '16px' : 0 }}>
              <table className="table">
                <caption className="visually-hidden">{sectionLabel('compliance')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('certificate')}</th>
                    <th scope="col">{t('number')}</th>
                    <th scope="col">{t('authority')}</th>
                    <th scope="col">{t('expires')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dpp.certificates.map((cert) => (
                    <tr key={cert.id}>
                      <td>{cert.certificate_type}</td>
                      <td>{cert.certificate_number || '—'}</td>
                      <td>{cert.issuing_authority || '—'}</td>
                      <td>{cert.expiry_date ? new Date(cert.expiry_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {dynamicFields.compliance.length > 0 ? (
            renderFieldGrid(dynamicFields.compliance)
          ) : (!dpp.certificates || dpp.certificates.length === 0) ? (
            <p className="text-muted">{t('noComplianceCerts')}</p>
          ) : null}
        </div>
      ) : null}

      {dynamicFields.technical.length > 0 && (
        <div className="card">
          <h2 className="section-title">{sectionLabel('technical')}</h2>
          {renderFieldGrid(dynamicFields.technical)}
        </div>
      )}

      <div className="card">
        <h2 className="section-title">{t('downloadableDocuments')}</h2>
        {documents.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <caption className="visually-hidden">{t('downloadableDocuments')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('name')}</th>
                  <th scope="col">{t('type')}</th>
                  <th scope="col">{t('size')}</th>
                  <th scope="col">{t('download')}</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(documentsByType).length > 0
                  ? Object.entries(documentsByType).flatMap(([documentType, typedDocuments]) =>
                      typedDocuments.map((doc, index) => (
                        <tr key={`${documentType}-${doc.name}-${index}`}>
                          <td>{doc.name}</td>
                          <td>{doc.document_type || doc.type || '—'}</td>
                          <td>{doc.size || '—'}</td>
                          <td>
                            {doc.url ? (
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                {t('open')}
                              </a>
                            ) : '—'}
                          </td>
                        </tr>
                      ))
                    )
                  : documents.map((doc, index) => (
                      <tr key={`${doc.name}-${index}`}>
                        <td>{doc.name}</td>
                        <td>{doc.document_type || doc.type || '—'}</td>
                        <td>{doc.size || '—'}</td>
                          <td>
                            {doc.url ? (
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                {t('open')}
                              </a>
                            ) : '—'}
                          </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted">{t('noDocuments')}</p>
        )}
      </div>

      <div className="card text-center mt-3" style={{ backgroundColor: hexToRgba(brandColor, 0.06) }}>
        <p className="text-muted" style={{ fontSize: '12px' }}>
          {brandName} · {t('poweredBy')}
        </p>
      </div>
        </main>
      </div>
    </>
  );
};

export default DPPViewer;
